import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import Anonymous from '../../../icons/Anonymous';
import { useColorMode } from '@chakra-ui/react';
import ChatHeader from './ChatHeader';
import ChatTextBubble from './ChatTextBubble';
import PictureGrid from '../../chat/components/PictureGrid';
import PhotoCardContent from '../../chat/components/PhotoCardContent';
import EventContent from '../../chat/components/EventContent';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import type { components } from '@pocapan/shared/types/schema';

type PostPreview = components['schemas']['PostPreview'];

export interface ChatUIProps {
  post: PostPreview;
  onFollowUser?: () => void;
  onLike?: () => void;
  onProfileClick?: () => void;
  onBubbleClick?: () => void;
  onComment?: () => void;
}

const ChatUI = ({
  post,
  onFollowUser,
  onLike,
  onProfileClick,
  onBubbleClick,
  onComment
}: ChatUIProps) => {
  const { colorMode } = useColorMode();
  const isOther = !post.isMine;

  const renderContent = () => {
    const contents: React.ReactNode[] = [];
    let keyIndex = 0;

    // megaphone이나 quotation일 때 내부 콘텐츠 렌더링
    const innerPost =
      post.postType === 'megaphone'
        ? post.megaphonePost
        : post.postType === 'quotation'
          ? post.quotedPost
          : null;

    if (innerPost) {
      if (innerPost.pictureUrls && innerPost.pictureUrls.length > 0) {
        contents.push(<PictureGrid key={`picture-${keyIndex++}`} urls={innerPost.pictureUrls} />);
      }
      if (innerPost.photoCard) {
        contents.push(
          <PhotoCardContent key={`photocard-${keyIndex++}`} photoCard={innerPost.photoCard} />
        );
      }
      if (innerPost.event) {
        contents.push(<EventContent key={`event-${keyIndex++}`} event={innerPost.event} />);
      }
      if (innerPost.content) {
        contents.push(
          <button
            key={`content-${keyIndex++}`}
            type="button"
            className="w-fit bg-pure-white dark:bg-pure-black text-left max-w-[212px] px-[16px] py-[12px] rounded-[16px] border border-surface-lv2"
            onClick={() => {
              if (innerPost.postId) {
                openNewTab(`/posts/${innerPost.postId}`);
              }
            }}
          >
            <p className="text-[13px] text-text-main-lv3 leading-[16px] line-clamp-3">
              {innerPost.content}
            </p>
          </button>
        );
      }
    }

    // post 콘텐츠 렌더링 (comment가 아닌 경우)
    if (post.postType !== 'comment') {
      if (post.pictureUrls && post.pictureUrls.length > 0) {
        contents.push(<PictureGrid key={`post-picture-${keyIndex++}`} urls={post.pictureUrls} />);
      }
      if (post.photoCard) {
        contents.push(
          <PhotoCardContent key={`post-photocard-${keyIndex++}`} photoCard={post.photoCard} />
        );
      }
      if (post.event) {
        contents.push(<EventContent key={`post-event-${keyIndex++}`} event={post.event} />);
      }
    }

    return contents;
  };

  return (
    <div className={`flex items-end ${isOther ? 'flex-row' : 'flex-row-reverse'} gap-[4px]`}>
      {/* 프로필 이미지 */}
      {isOther && (
        <button type="button" onClick={() => onProfileClick?.()}>
          {post.anonymous ? (
            <Anonymous size={24} currentColor={colorMode === 'dark' ? '#AAABAC' : '#666666'} />
          ) : (
            <ProfileAvatar
              thumbnailUrl={post.authorThumbnailUrl ?? undefined}
              widthAndHeight="26px"
              svgWidthAndHeight="24px"
            />
          )}
        </button>
      )}
      <div className={`flex flex-col gap-[4px] ${isOther ? 'items-start' : 'items-end'}`}>
        <ChatHeader isOther={isOther} post={post} onFollowUser={onFollowUser} />
        <ChatTextBubble
          isOther={isOther}
          text={post.content ?? undefined}
          likesCount={post.likesCount}
          commentsCount={post.commentsCount}
          isLiked={post.isLiked}
          onLike={onLike}
          onComment={onComment}
          onBubbleClick={onBubbleClick}
          timeStamp={post.authoredTimestamp as number}
        >
          {renderContent()}
        </ChatTextBubble>
      </div>
    </div>
  );
};

export default ChatUI;
